import React from 'react'
import {
  BrowserRouter as Router, Route, RouteProps, Switch, Redirect,
} from 'react-router-dom'
import Layout from 'layouts/Layout'
import {
  ApolloProvider,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  HttpLink,
  concat,
} from '@apollo/client'
import { Token, SessionProvider, useSession } from 'components/auth/Session'
import { ToastContainer } from 'components/common/ToastContainer'
import DashboardPage from 'pages/Dashboard'
import AuthBasicLayout from 'layouts/AuthBasicLayout'
import CreateSpacePage from 'pages/spaces/create'
import CreateCasePage from 'pages/tracing/create'
import SpacePage from 'pages/spaces/read'
import ListSpacesPage from 'pages/spaces/list'
import ListTracingPage from 'pages/tracing/list'
import CasePage from 'pages/tracing/read'
import SettingsPage from 'pages/Settings'
import { FeaturesProvider } from 'components/FeaturesContext'
import AuthComponent from 'components/auth/Auth'
import SignupForm from 'components/signup/SignupForm'
import QCStatsPage from 'pages/QCStatsPage'

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = Token
  if (token) {
    operation.setContext({
      headers: {
        authorization: token,
      },
    })
  }
  return forward(operation)
})

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000'

const client = new ApolloClient({
  cache: new InMemoryCache(),
  typeDefs: 'generated/types.tsx',
  link: concat(
    authMiddleware,
    new HttpLink({
      uri: apiUrl,
    }),
  ),
})

const RouteWithLayout: React.FC<RouteProps & { authenticated?: boolean }> = ({
  children, authenticated = true, location, ...rest
}) => {
  const session = useSession()

  if (authenticated && !session.user) {
    return (
      <Redirect
        to={{
          pathname: '/auth/login',
          state: { from: location },
        }}
      />
    )
  }

  return (
    <Route {...rest}>
      <Layout>
        {children}
      </Layout>
    </Route>
  )
}

const App = () => (
  <ApolloProvider client={client}>
    <ToastContainer position="top-center" />
    <SessionProvider>
      <FeaturesProvider>
        <Router basename={process.env.PUBLIC_URL}>
          <Switch>
            <Route path="/auth">
              <AuthBasicLayout>
                <AuthComponent />
              </AuthBasicLayout>
            </Route>

            <Route path="/signup">
              <AuthBasicLayout>
                <SignupForm />
              </AuthBasicLayout>
            </Route>

            <RouteWithLayout path="/" exact>
              <DashboardPage />
            </RouteWithLayout>

            <RouteWithLayout path="/spaces" exact>
              <ListSpacesPage />
            </RouteWithLayout>

            <RouteWithLayout path="/spaces/:id">
              <SpacePage />
            </RouteWithLayout>

            <RouteWithLayout path="/space">
              <CreateSpacePage />
            </RouteWithLayout>

            <RouteWithLayout path="/case/:id">
              <CasePage />
            </RouteWithLayout>

            <RouteWithLayout path="/case">
              <CreateCasePage />
            </RouteWithLayout>

            <RouteWithLayout path="/tracing">
              <ListTracingPage />
            </RouteWithLayout>

            <RouteWithLayout path="/settings">
              <SettingsPage />
            </RouteWithLayout>

            <RouteWithLayout path="/qcs">
              <QCStatsPage />
            </RouteWithLayout>
          </Switch>
        </Router>
      </FeaturesProvider>
    </SessionProvider>
  </ApolloProvider>
)

export default App
